import { memo, useCallback, useRef } from 'react';
import { Truck, Store, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useDeliveryStore } from '../model/use-delivery-store';
import type { CdekDeliveryInfo, DoorAddress } from '../model/use-delivery-store';
import { CdekSelectedPoint } from './cdek-delivery-picker';
import { DoorAddressForm } from './door-address-form';

interface CdekDeliverySectionProps {
  pickupAddress?: string;
  className?: string;
}

export const CdekDeliverySection = memo(function CdekDeliverySection({
  pickupAddress = 'ул. Советская, 15',
  className,
}: CdekDeliverySectionProps) {
  const method = useDeliveryStore(s => s.method);
  const cdekInfo = useDeliveryStore(s => s.cdekInfo);
  const doorFormOpen = useDeliveryStore(s => s.doorFormOpen);
  const widgetOpen = useDeliveryStore(s => s.widgetOpen);
  const doorAddress = useDeliveryStore(s => s.doorAddress);

  const selectPickup = useDeliveryStore(s => s.selectPickup);
  const selectCdek = useDeliveryStore(s => s.selectCdek);
  const changePoint = useDeliveryStore(s => s.changePoint);
  const setDoorAddress = useDeliveryStore(s => s.setDoorAddress);

  const isPickup = method === 'pickup';

  const cdekInfoRef = useRef(cdekInfo);
  cdekInfoRef.current = cdekInfo;

  const handleDoorAddress = useCallback((addr: DoorAddress) => {
    setDoorAddress(addr);
  }, [setDoorAddress]);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Compact row options */}
      <div className="space-y-2">
        {/* Pickup row */}
        <button
          type="button"
          onClick={selectPickup}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-all active:scale-[0.99]',
            isPickup
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-silk/60 dark:border-dark-border bg-white dark:bg-dark-card',
          )}
        >
          <div className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
            isPickup ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
          )}>
            <Store className="w-4 h-4" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-[14px] font-semibold text-ink dark:text-white leading-tight">Самовывоз</p>
            <p className="text-[12px] text-muted-foreground truncate">{pickupAddress}</p>
          </div>
          <div className="shrink-0 text-right">
            {isPickup ? (
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            ) : (
              <span className="text-[12px] font-semibold text-emerald-600">Бесплатно</span>
            )}
          </div>
        </button>

        {/* CDEK row */}
        <button
          type="button"
          onClick={selectCdek}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-all active:scale-[0.99]',
            !isPickup
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-silk/60 dark:border-dark-border bg-white dark:bg-dark-card',
          )}
        >
          <div className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
            !isPickup ? 'bg-[#00B33C] text-white' : 'bg-[#00B33C]/10 text-[#00B33C]',
          )}>
            <Truck className="w-4 h-4" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-[14px] font-semibold text-ink dark:text-white leading-tight">СДЭК</p>
            <p className="text-[12px] text-muted-foreground">По всей России</p>
          </div>
          <div className="shrink-0 text-right">
            {!isPickup ? (
              cdekInfo ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-semibold text-primary">~{cdekInfo.cost} ₽</span>
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>
      </div>

      {/* CDEK point info + door form */}
      {!isPickup && !widgetOpen && (
        <div className="space-y-3">
          {cdekInfo && (
            <CdekSelectedPoint deliveryInfo={cdekInfo} onChangeClick={changePoint} />
          )}
          {cdekInfo?.type === 'DOOR' && doorFormOpen && (
            <div className="p-4 rounded-xl bg-paper dark:bg-dark-card border border-silk/50 dark:border-dark-border">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
                Адрес доставки
              </p>
              <DoorAddressForm value={doorAddress} onChange={handleDoorAddress} />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export type { CdekDeliveryInfo, DoorAddress };
